import { useEffect, useState } from "react";
import { api, API_BASE, formatApiErrorDetail } from "@/lib/api";
import { formatWIB } from "@/lib/time";
import { Check, X, Loader2, ClipboardCheck, MapPin } from "lucide-react";
import { toast } from "sonner";

export default function ReportsReview() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewer, setViewer] = useState(null);
  const [processing, setProcessing] = useState(null);
  const token = localStorage.getItem("aroma_token");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/field-reports?status=pending");
      setRows(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const approve = async (id) => {
    setProcessing(id);
    try {
      const { data } = await api.post(`/field-reports/${id}/approve`);
      toast.success(`Disetujui — tiket ${data.no_tiket} dibuat`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setProcessing(null); }
  };
  const reject = async (id) => {
    if (!window.confirm("Tolak laporan ini?")) return;
    setProcessing(id);
    try {
      await api.post(`/field-reports/${id}/reject`);
      toast.success("Laporan ditolak");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setProcessing(null); }
  };

  const photoUrl = (r) => `${API_BASE}/field-reports/${r.id}/photo?auth=${encodeURIComponent(token || "")}`;

  return (
    <div className="space-y-6" data-testid="reports-review-page">
      <div>
        <div className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#9CA3AF]">Verifikasi Lapangan</div>
        <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tighter mt-1 flex items-center gap-2">
          <ClipboardCheck className="h-7 w-7 text-[#0055FF]" /> Persetujuan Temuan
        </h1>
        <p className="text-sm text-[#4B5563] mt-2">
          {rows.length} laporan menunggu keputusan. Setujui → otomatis jadi tiket resmi.
        </p>
      </div>

      {loading && <div className="text-sm text-[#9CA3AF]">Memuat...</div>}
      {!loading && rows.length === 0 && (
        <div className="text-center py-16 text-[#9CA3AF] border border-dashed border-[#E5E7EB] rounded-md">
          Tidak ada laporan menunggu persetujuan.
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {rows.map((r) => (
          <div key={r.id} className="bg-white border border-[#E5E7EB] rounded-md overflow-hidden flex flex-col" data-testid={`report-${r.id}`}>
            <img
              src={photoUrl(r)}
              alt="temuan"
              className="w-full h-48 object-cover cursor-zoom-in"
              onClick={() => setViewer(r)}
            />
            <div className="p-4 flex-1 flex flex-col gap-2">
              <div className="text-[10px] font-bold uppercase tracking-widest text-[#9CA3AF]">
                {formatWIB(r.created_at, { year: undefined })}
              </div>
              <div className="text-sm text-[#0A0A0A] line-clamp-3">{r.description}</div>
              <div className="flex items-center gap-2 text-xs text-[#4B5563] flex-wrap">
                <span className="inline-flex items-center gap-1 bg-[#0055FF]/10 text-[#0044CC] font-semibold px-2 py-0.5 rounded-md">
                  <MapPin className="h-3 w-3" />
                  {r.wilayah || "—"}
                </span>
                <span className="text-[10px] text-[#9CA3AF] font-mono-tab">oleh {r.created_by_name}</span>
              </div>
              <div className="flex gap-2 pt-3 mt-auto border-t border-[#F3F4F6]">
                <button
                  onClick={() => reject(r.id)}
                  disabled={processing === r.id}
                  className="flex-1 h-9 rounded-md border border-[#E5E7EB] text-[#991B1B] hover:bg-[#FEE2E2] text-xs font-semibold flex items-center justify-center gap-1"
                  data-testid={`btn-reject-${r.id}`}
                >
                  <X className="h-3.5 w-3.5" /> Tolak
                </button>
                <button
                  onClick={() => approve(r.id)}
                  disabled={processing === r.id}
                  className="flex-1 h-9 rounded-md bg-[#0055FF] hover:bg-[#0044CC] text-white text-xs font-semibold flex items-center justify-center gap-1"
                  data-testid={`btn-approve-${r.id}`}
                >
                  {processing === r.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                  Setujui
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {viewer && (
        <div className="fixed inset-0 bg-black/85 z-[9999] flex items-center justify-center p-4" onClick={() => setViewer(null)}>
          <img src={photoUrl(viewer)} alt="" className="max-h-full max-w-full object-contain" />
        </div>
      )}
    </div>
  );
}
